import { useRef, useEffect, useCallback, useState } from 'react';

interface UseLottieOptions {
    path: string;
    useAutoplay?: boolean;
    loop?: boolean;
    root?: Element | null;
}

interface LottieStatus {
    isPlaying: boolean;
    isPaused: boolean;
    isStopped: boolean;
    currentFrame: number;
    totalFrames: number;
    currentProgress: number; // 0 ~ 1
}

const useLottie = (options: UseLottieOptions) => {
    const { path, useAutoplay = true, root = null, loop = false } = options;
    const containerRef = useRef<HTMLDivElement>(null);
    const instanceRef = useRef<any>(null);
    const destroyRef = useRef<(() => void) | null>(null);
    const [status, setStatus] = useState<LottieStatus>({
        isPlaying: false,
        isPaused: false,
        isStopped: true,
        currentFrame: 0,
        totalFrames: 0,
        currentProgress: 0,
    });

    const updateStatus = useCallback(() => {
        if (!instanceRef.current) return;

        const instance = instanceRef.current;
        const isPaused = instance.isPaused ?? false;
        const isStopped = instance.isStopped ?? false;
        const isPlaying = !isPaused && !isStopped;
        const currentFrame = instance.currentFrame ?? 0;
        const totalFrames = instance.totalFrames ?? 0;
        const currentProgress = totalFrames > 0 ? currentFrame / totalFrames : 0;

        setStatus({
            isPlaying,
            isPaused,
            isStopped,
            currentFrame,
            totalFrames,
            currentProgress,
        });
    }, []);

    const clearStatus = useCallback(() => {
      setStatus({
        isPlaying: false,
        isPaused: false,
        isStopped: true,
        currentFrame: 0,
        totalFrames: 0,
        currentProgress: 0,
      });
    }, []);


    useEffect(() => {
      if (!containerRef.current) return;

      // buildLottieAnimation 함수 직접 호출
      const result = buildLottieAnimation(root ?? document.getElementById('hMainContainer'), containerRef.current, path, useAutoplay, loop);

      instanceRef.current = result.instance;
      destroyRef.current = result.destory;

      // 초기 상태 업데이트
      if (result.instance) {
        updateStatus();

        // 이벤트 리스너 등록하여 상태 변화 감지
        const instance = result.instance;

        const handleEnterFrame = () => {
          updateStatus();
        };

        const handleComplete = () => {
          updateStatus();
        };

        const handleLoopComplete = () => {
          updateStatus();
        };

        instance.addEventListener('enterFrame', handleEnterFrame);
        instance.addEventListener('complete', handleComplete);
        instance.addEventListener('loopComplete', handleLoopComplete);

        // 인스턴스가 mount된 후 play 호출
        if (useAutoplay) {
          requestAnimationFrame(() => {
            // 추가로 한 프레임 더 대기하여 DOM이 완전히 렌더링되도록 함
            requestAnimationFrame(() => {
              if (instanceRef.current) {
                instanceRef.current.play();
                updateStatus();
              }
            });
          });
        }

        // cleanup 함수
        return () => {
          instance.removeEventListener('enterFrame', handleEnterFrame);
          instance.removeEventListener('complete', handleComplete);
          instance.removeEventListener('loopComplete', handleLoopComplete);

          if (destroyRef.current) {
            destroyRef.current();
          }

          clearStatus();
        };
      }
    }, [path, useAutoplay, root, loop, updateStatus, clearStatus]);

    const play = useCallback(() => {
        if (instanceRef.current) {
            instanceRef.current.play();
            updateStatus();
        }
    }, [updateStatus]);

    const pause = useCallback(() => {
        if (instanceRef.current) {
            instanceRef.current.pause();
            updateStatus();
        }
    }, [updateStatus]);

    const stop = useCallback(() => {
        if (instanceRef.current) {
            instanceRef.current.stop();
            updateStatus();
        }
    }, [updateStatus]);

    const getStatus = useCallback((): LottieStatus => {
        if (!instanceRef.current) {
            return status;
        }

        const instance = instanceRef.current;
        const isPaused = instance.isPaused ?? false;
        const isStopped = instance.isStopped ?? false;
        const isPlaying = !isPaused && !isStopped;
        const currentFrame = instance.currentFrame ? instance.currentFrame + 1 : 0;
        const totalFrames = instance.totalFrames ?? 0;
        const currentProgress = totalFrames > 0 ? currentFrame / totalFrames : 0;

        return {
            isPlaying,
            isPaused,
            isStopped,
            currentFrame,
            totalFrames,
            currentProgress,
        };
    }, [status]);

    return {
        element: containerRef,
        play,
        pause,
        stop,
        status,
        getStatus,
    };
};

export default useLottie;